/**
 * Application Configuration
 * Central place for all app-wide settings
 */

// Runtime config (injected at container startup for Docker)
declare global {
  interface Window {
    __ENV__?: {
      NEXT_PUBLIC_API_URL?: string;
      NEXT_PUBLIC_GOOGLE_CLIENT_ID?: string;
    };
  }
}

// Helper to get runtime or build-time env var
function getEnvVar(key: 'NEXT_PUBLIC_API_URL' | 'NEXT_PUBLIC_GOOGLE_CLIENT_ID', defaultValue: string = ''): string {
  // Try runtime config first (for Docker)
  if (typeof window !== 'undefined' && window.__ENV__?.[key]) {
    return window.__ENV__[key] || defaultValue;
  }
  
  // Fall back to build-time env var (for local dev)
  // IMPORTANT: Next.js requires static access to process.env.NEXT_PUBLIC_*
  // Dynamic key lookup (process.env[key]) does NOT work!
  let envValue: string | undefined;
  if (key === 'NEXT_PUBLIC_API_URL') {
    envValue = process.env.NEXT_PUBLIC_API_URL;
  } else if (key === 'NEXT_PUBLIC_GOOGLE_CLIENT_ID') {
    envValue = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  }
  
  // If we're in development and env is empty, check for localhost or dev domain in window.location
  if (!envValue && key === 'NEXT_PUBLIC_API_URL' && typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000/api';
    }
    if (hostname === 'dev.storita.ai') {
      return 'https://dev.storita.ai/api';
    }
  }
  
  return envValue || defaultValue;
}

// Base path for authenticated routes (no basePath in Next.js config anymore)
export const APP_BASE_PATH = '';

// =================================================================
// IMPORTANT: Domain Configuration - DO NOT CHANGE WITHOUT READING!
// -----------------------------------------------------------------
// PRODUCTION:  https://storita.ai (frontend + backend)
// DEVELOPMENT: https://dev.storita.ai (frontend + backend)
//
// The default URL MUST be the PRODUCTION domain (storita.ai).
// This ensures production builds work correctly out of the box.
// 
// For local development, set NEXT_PUBLIC_API_URL in your .env.local:
//   NEXT_PUBLIC_API_URL=http://localhost:8000/api
//
// ⚠️  DO NOT change 'storita.ai' to 'dev.storita.ai' here!
// ⚠️  That will break production! It has happened before!
// =================================================================
export const API_CONFIG = {
  get baseUrl(): string {
    return getEnvVar('NEXT_PUBLIC_API_URL', 'https://storita.ai/api');
  },
  timeout: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || '60000'),
};

// Google OAuth Configuration
// Use getter to ensure runtime evaluation after window.__ENV__ is loaded
export const GOOGLE_CONFIG = {
  get clientId(): string {
    return getEnvVar('NEXT_PUBLIC_GOOGLE_CLIENT_ID', '');
  },
};

/**
 * Normalize the configured API base URL so it never ends with `/api` or a trailing slash.
 * This lets callers consistently append their own path segments without worrying
 * about duplicate prefixes like `/api/api`.
 */
export function getNormalizedApiBaseUrl(): string {
  // Get baseUrl fresh each time to support runtime env injection (window.__ENV__)
  const rawBase = getEnvVar('NEXT_PUBLIC_API_URL', 'https://storita.ai/api');
  const trimmed = rawBase.replace(/\/+$/, '');

  if (trimmed.toLowerCase().endsWith('/api')) {
    return trimmed.slice(0, -4);
  }

  return trimmed;
}

/**
 * Get API URL with /api prefix
 * All backend endpoints are mounted under /api
 * @param path - The path without leading slash (e.g., 'auth/login')
 * @returns Full API URL (e.g., 'http://localhost:8000/api/auth/login')
 */
export function getApiUrl(path: string): string {
  // Get baseUrl fresh each time to support runtime env injection
  const rawBase = getEnvVar('NEXT_PUBLIC_API_URL', 'https://storita.ai/api');
  const trimmed = rawBase.replace(/\/+$/, '');
  const base = trimmed.toLowerCase().endsWith('/api') ? trimmed.slice(0, -4) : trimmed;
  const cleanPath = path.replace(/^\/+/, '');
  return `${base}/api/${cleanPath}`;
}

// Route paths - clean routes without /app prefix
export const ROUTES = {
  home: '/',
  login: '/login',
  register: '/register',
  chat: '/chat',
  analytics: '/analytics',
  questionsManager: '/ai-questions-manager',
  tools: '/tools',
  settings: '/settings',
  users: '/users',
  instructions: '/ai-prompt-config',
  debug: '/debug',
} as const;

// Get full path (now routes already include /app prefix where needed)
export function getFullPath(route: keyof typeof ROUTES): string {
  return ROUTES[route];
}

// Check if current pathname matches a route
export function isRoute(pathname: string, route: keyof typeof ROUTES): boolean {
  const fullPath = getFullPath(route);
  return pathname === fullPath || pathname === ROUTES[route];
}

// Application settings
export const APP_CONFIG = {
  name: 'Storita AI',
  version: process.env.NEXT_PUBLIC_APP_VERSION || '0.0.0',
  
  // Query client settings
  queryClient: {
    staleTime: 5 * 60 * 1000, // 5 minutes (was 1 minute)
    gcTime: 15 * 60 * 1000,   // 15 minutes garbage collection
    retry: 1,
  },
  
  // Toast notification settings
  toast: {
    position: 'top-right' as const,
    duration: 3000,
    style: {
      background: '#1e293b',
      color: '#f1f5f9',
      border: '1px solid #334155',
    },
  },
};

export default {
  APP_BASE_PATH,
  API_CONFIG,
  ROUTES,
  APP_CONFIG,
  getFullPath,
  isRoute,
};
